// TabbedEditor.js
// Tabbed editor interface for Zolaid IDE
import React, { useState, useRef } from 'react';
import MonacoEditor from './MonacoEditor';
import CodeSuggestionBar from './CodeSuggestionBar';
import { fetchAISuggestion } from './aiSuggest';
import { applyPatchToCode } from './aiPatch';

const TabbedEditor = React.forwardRef(({ theme = 'vs-dark', minimap = true }, ref) => {
  const [tabs, setTabs] = useState([]); // [{path, name, content}]
  const [activeTab, setActiveTab] = useState(null);
  const [draggedTab, setDraggedTab] = useState(null);
  const [renamingTab, setRenamingTab] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [suggestion, setSuggestion] = useState('');
  const [suggesting, setSuggesting] = useState(false);
  const lastCodeRef = useRef('');

  const openFile = (path) => {
    // If already open, just activate
    const existing = tabs.find((t) => t.path === path);
    if (existing) {
      setActiveTab(existing.path);
      return;
    }
    // Placeholder: Replace with real file loading
    const name = path.split('/').pop();
    const content = '// File: ' + path + '\n';
    const newTab = { path, name, content };
    setTabs([...tabs, newTab]);
    setActiveTab(path);
  };

  const closeTab = (path) => {
    const idx = tabs.findIndex((t) => t.path === path);
    const newTabs = tabs.filter((t) => t.path !== path);
    setTabs(newTabs);
    if (activeTab === path) {
      if (newTabs.length) setActiveTab(newTabs[Math.max(0, idx - 1)].path);
      else setActiveTab(null);
    }
  };

  const updateContent = async (path, newContent) => {
    setTabs(tabs.map((t) => (t.path === path ? { ...t, content: newContent } : t)));
    if (path === activeTab) {
      // Only suggest for active tab
      if (newContent !== lastCodeRef.current) {
        setSuggesting(true);
        setSuggestion('');
        const lang = path.endsWith('.js') ? 'javascript' : path.endsWith('.py') ? 'python' : 'plaintext';
        const aiSuggestion = await fetchAISuggestion({ code: newContent, language: lang });
        setSuggestion(aiSuggestion);
        setSuggesting(false);
        lastCodeRef.current = newContent;
      }
    }
  };
  const handleAcceptSuggestion = () => {
    if (!suggestion) return;
    setTabs(tabs.map((t) =>
      t.path === activeTab ? { ...t, content: t.content + (t.content.endsWith('\n') ? '' : '\n') + suggestion } : t
    ));
    setSuggestion('');
  };

  const handleRejectSuggestion = () => {
    setSuggestion('');
  };

  // Expose openFile to parent via ref
  // Expose openFile and AI patching to parent via ref
  React.useImperativeHandle(ref, () => ({
    openFile,
    applyAIPatch: async (patchArr) => {
      if (!activeTab) return;
      let tab = tabs.find(t => t.path === activeTab);
      if (!tab) return;
      let code = tab.content;
      for (let i = 0; i < patchArr.length; ++i) {
        code = applyPatchToCode(code, [patchArr[i]]);
        setTabs(tabs => tabs.map(t => t.path === activeTab ? { ...t, content: code } : t));
        // Wait a bit to animate
        // eslint-disable-next-line no-loop-func
        await new Promise(res => setTimeout(res, 350));
      }
    }
  }));

  // Drag-and-drop handlers
  const handleDragStart = (e, tab) => {
    setDraggedTab(tab.path);
    e.dataTransfer.effectAllowed = 'move';
  };
  const handleDragOver = (e, overTab) => {
    e.preventDefault();
    if (draggedTab && draggedTab !== overTab.path) {
      const fromIdx = tabs.findIndex(t => t.path === draggedTab);
      const toIdx = tabs.findIndex(t => t.path === overTab.path);
      if (fromIdx !== -1 && toIdx !== -1) {
        const newTabs = [...tabs];
        const [moved] = newTabs.splice(fromIdx, 1);
        newTabs.splice(toIdx, 0, moved);
        setTabs(newTabs);
      }
    }
  };
  const handleDragEnd = () => setDraggedTab(null);

  // File rename handlers
  const startRename = (tab) => {
    setRenamingTab(tab.path);
    setRenameValue(tab.name);
  };
  const finishRename = (tab) => {
    setTabs(tabs.map(t => t.path === tab.path ? { ...t, name: renameValue } : t));
    setRenamingTab(null);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ display: 'flex', background: '#232323', color: '#fff', borderBottom: '1px solid #222' }}>
        {tabs.map((tab) => (
          <div
            key={tab.path}
            style={{
              padding: '8px 16px',
              background: tab.path === activeTab ? '#181818' : 'transparent',
              borderRight: '1px solid #222',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              opacity: draggedTab === tab.path ? 0.5 : 1,
            }}
            onClick={() => setActiveTab(tab.path)}
            draggable
            onDragStart={e => handleDragStart(e, tab)}
            onDragOver={e => handleDragOver(e, tab)}
            onDragEnd={handleDragEnd}
            onDoubleClick={() => startRename(tab)}
          >
            {renamingTab === tab.path ? (
              <input
                value={renameValue}
                autoFocus
                onChange={e => setRenameValue(e.target.value)}
                onBlur={() => finishRename(tab)}
                onKeyDown={e => { if (e.key === 'Enter') finishRename(tab); }}
                style={{ width: 90, fontSize: 15, borderRadius: 4, border: 'none', padding: '2px 6px' }}
              />
            ) : (
              tab.name
            )}
            <span
              style={{ marginLeft: 8, color: '#888', cursor: 'pointer' }}
              onClick={(e) => {
                e.stopPropagation();
                closeTab(tab.path);
              }}
            >
              ×
            </span>
          </div>
        ))}
      </div>
      <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
        {tabs.map(
          (tab) =>
            tab.path === activeTab && (
              <>
                <MonacoEditor
                  key={tab.path}
                  value={tab.content}
                  onChange={(val) => updateContent(tab.path, val)}
                  theme={theme}
                  minimap={minimap}
                />
                <CodeSuggestionBar
                  suggestion={suggestion}
                  onAccept={handleAcceptSuggestion}
                  onReject={handleRejectSuggestion}
                />
              </>
            )
        )}
      </div>
    </div>
  );
});

export default TabbedEditor;
